import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import StorageManager from '../utils/StorageManager';
import SoundManager from '../utils/SoundManager';
import './LevelSelectScreen.css';

/**
 * WIP_LEVEL_THRESHOLD
 * Niveis com numero >= a este valor exibem o badge "Em construcao".
 * Para remover todos os badges: defina como Infinity.
 * Para mover o corte para o nivel 5: mude para 5.
 */
const WIP_LEVEL_THRESHOLD = 4;

// Variável de módulo: garante que o som de abertura toque apenas no primeiro carregamento do aplicativo
let hasPlayedOpeningSound = false;

const LevelSelectScreen = ({ onSelectLevel, onNavigateToStats }) => {
  const [niveis, setNiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelStats, setLevelStats] = useState({});
  // ID do nivel cujo tooltip WIP está aberto (hover/toque)
  const [wipTooltipOpen, setWipTooltipOpen] = useState(null);

  // Tocar som de abertura apenas ao abrir a aplicação pela primeira vez (jogo fechado -> abrir)
  useEffect(() => {
    if (!hasPlayedOpeningSound) {
      hasPlayedOpeningSound = true;
      SoundManager.play('opening');
    }
  }, []);

  useEffect(() => {
    const loadNiveis = async () => {
      try {
        const niveisQuery = query(
          collection(db, 'niveis'),
          orderBy('nivel', 'asc')
        );
        
        const snapshot = await getDocs(niveisQuery);
        const niveisData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setNiveis(niveisData);
      } catch (error) {
        console.error('Erro ao carregar níveis:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNiveis();
  }, []);

  // Carregar estatísticas de todos os níveis
  useEffect(() => {
    if (niveis.length > 0) {
      const stats = {};
      niveis.forEach(nivel => {
        stats[nivel.id] = StorageManager.getLevelStats(nivel.id);
      });
      setLevelStats(stats);
    }
  }, [niveis]);

  // Função para verificar se um nível está desbloqueado
  const isLevelUnlocked = (nivel) => {
    // Nível 1 sempre desbloqueado
    if (nivel.nivel === 1) return true;
    
    // Encontrar nível anterior
    const previousNivel = niveis.find(n => n.nivel === nivel.nivel - 1);
    if (!previousNivel) return true; // Fallback: se não encontrar anterior, desbloqueia
    
    // Verificar se nível anterior foi completado
    const previousStats = levelStats[previousNivel.id] || {};
    return previousStats.completed === true;
  };

  if (loading) {
    return (
      <div className="level-select-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Carregando níveis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="level-select-screen">
      <div className="level-select-container">
        {/* Barra de Navegação de Saída para o Portal Winiverso */}
        <div className="top-nav-bar">
          <a href="https://www.winiverso.com/" className="btn-exit-winiverso" title="Voltar ao portal Winiverso">
            ← Sair para o Winiverso
          </a>
        </div>

        {/* Cabeçalho Afrofuturista AAA */}
        <div className="title-wrapper">
          <div className="adinkra-header-symbol left">✦</div>
          <h1 className="game-title">MEMÓRIA DOS IMORTAIS</h1>
          <div className="adinkra-header-symbol right">✦</div>
        </div>
        <p className="game-subtitle">
          <span className="subtitle-glow">Escolha seu desafio</span>
        </p>

        {niveis.length > 0 && (
          (() => {
            const totalLevels = niveis.length;
            const completedCount = Object.values(levelStats).filter(s => s && s.completed).length;
            const percentage = Math.round((completedCount / totalLevels) * 100);
            return (
              <div className="progress-indicator" aria-label={`Progresso: ${completedCount} de ${totalLevels} níveis`}>
                <span className="progress-text">PROGRESSO: {completedCount}/{totalLevels}</span>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })()
        )}

        <div className="levels-grid">
          {niveis.length > 0 ? (
            niveis.map((nivel, index) => {
              const stats = levelStats[nivel.id] || {};
              const hasHighScore = stats.highScore > 0;
              const isCompleted = stats.completed;
              const isUnlocked = isLevelUnlocked(nivel);

              const isWip = nivel.nivel >= WIP_LEVEL_THRESHOLD;
              const isTooltipOpen = wipTooltipOpen === nivel.id;

              return (
                <div
                  key={nivel.id}
                  className={`level-card ${
                    isUnlocked ? 'level-card-unlocked' : 'level-card-locked'
                  } ${
                    isCompleted ? 'level-card-completed' : ''
                  } ${
                    isWip ? 'level-card-wip' : ''
                  } ${
                    isTooltipOpen ? 'wip-tooltip-open' : ''
                  }`}
                  style={{ animationDelay: `${index * 80}ms` }}
                  onClick={() => {
                    if (isWip && !isTooltipOpen) {
                      // Primeiro toque em mobile: abre tooltip em vez de navegar
                      setWipTooltipOpen(nivel.id);
                      return;
                    }
                    if (isUnlocked) {
                      setWipTooltipOpen(null);
                      onSelectLevel(nivel.id);
                    }
                  }}
                  onMouseEnter={() => isWip && setWipTooltipOpen(nivel.id)}
                  onMouseLeave={() => setWipTooltipOpen(null)}
                >
                  {/* Facetas de luz low-poly nos cantos */}
                  <div className="card-hex-corner top-left"></div>
                  <div className="card-hex-corner top-right"></div>
                  <div className="card-hex-corner bottom-left"></div>
                  <div className="card-hex-corner bottom-right"></div>

                  {/* Badge WIP — "Em Construção" (nível 4+) */}
                  {isWip && (
                    <div
                      className="wip-badge"
                      aria-label="Conteúdo em construção"
                      role="note"
                    >
                      <span className="wip-badge-icon">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                      </span>
                      Em breve
                    </div>
                  )}

                  {/* Tooltip WIP — aparece no hover/toque */}
                  {isWip && isTooltipOpen && (
                    <div className="wip-tooltip" role="tooltip">
                      <p className="wip-tooltip-title">🚧 Personagens a caminho!</p>
                      <p className="wip-tooltip-body">
                        Estamos convocando novos imortais para este desafio.
                        Em breve eles chegarão com suas histórias e
                        biografias incríveis!
                      </p>
                      <p className="wip-tooltip-footer">
                        Enquanto isso... que tal repetir os níveis anteriores? 😄
                      </p>
                    </div>
                  )}

                  {/* Ícone de cadeado esculpido em ouro low-poly com Gye Nyame Adinkra emblem */}
                  {!isUnlocked && (
                    <div className="level-lock-icon" title="Complete o nível anterior para desbloquear">
                      <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="5" y="11" width="14" height="10" rx="2" fill="#d4af37" fillOpacity="0.2" stroke="#d4af37"/>
                        <path d="M8 11V7a4 4 0 018 0v4" stroke="#d4af37"/>
                        <circle cx="12" cy="15" r="1.5" fill="#00e5ff"/>
                        <path d="M12 16.5V19" stroke="#00e5ff"/>
                      </svg>
                    </div>
                  )}

                  {/* Badge de completado em ouro/verde */}
                  {isCompleted && (
                    <div className="level-badge" title="Nível Completado">
                      ✓
                    </div>
                  )}

                  <div className="level-number">NÍVEL {nivel.nivel}</div>
                  <div className="level-title">{nivel.titulo}</div>
                  <div className="level-info level-metrics">
                    <div className="info-item">
                      <span className="info-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#00e5ff" strokeWidth="2">
                          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                          <polygon points="12 12 2 17 12 22 22 17 12 12"/>
                        </svg>
                      </span>
                      <span className="info-text">{nivel.pares} pares</span>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#d4af37" strokeWidth="2">
                          <circle cx="12" cy="12" r="9"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </span>
                      <span className="info-text">{nivel.tempo}s</span>
                    </div>
                  </div>

                  {/* High Score do nível */}
                  {hasHighScore && (
                    <div className="level-high-score">
                      <span className="high-score-icon">🏆</span>
                      <span className="high-score-value">
                        {stats.highScore.toLocaleString()}
                      </span>
                    </div>
                  )}

                  {/* Estatísticas adicionais */}
                  {stats.timesPlayed > 0 && (
                    <div className="level-stats-mini">
                      <span title={`Jogado ${stats.timesPlayed} vezes`}>
                        🎮 {stats.timesPlayed}
                      </span>
                      {stats.timesWon > 0 && (
                        <span title={`${stats.timesWon} vitórias`}>
                          ✅ {stats.timesWon}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-levels">
              <p>Nenhum nível disponível.</p>
              <p className="hint">Configure os níveis no Firebase Firestore.</p>
            </div>
          )}
        </div>

        {/* Botão de Estatísticas */}
        {niveis.length > 0 && (
          <div className="stats-button-container">
            <button 
              className="btn-statistics" 
              onClick={() => onNavigateToStats && onNavigateToStats()}
            >
              <span className="btn-icon">📊</span>
              <span className="btn-text">Ver Estatísticas Completas</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelSelectScreen;
